/*
  Background video for the hero banner "Cinematic" layout.
  The poster image is the page's first paint; the video only starts downloading once the page has fully loaded and
  the browser is idle, so it never competes with the poster, styles or fonts. One MP4 source is chosen (the lightest
  on phones, up to 720p elsewhere). It stays on the poster for reduced motion, Save-Data or 2G connections.
*/
(() => {
  const isPhone = () => window.matchMedia('(max-width: 749px)').matches;
  const allowed = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return true;
    return !(c.saveData || c.effectiveType === 'slow-2g' || c.effectiveType === '2g');
  };
  const height = (url) => {
    const m = (url || '').match(/(\d{3,4})p/i);
    return m ? parseInt(m[1], 10) : 720;
  };
  // Shopify video files often list only the HD rendition; its SD sibling usually exists. Falls back on error.
  const lighter = (url) => {
    const m = url && url.match(/\.HD-\d+p-.+?-(\d+)\.mp4/i);
    return m ? url.replace(/\.HD-\d+p-.+?\.mp4/i, '.SD-480p-1.2Mbps-' + m[1] + '.mp4') : url;
  };

  const play = (wrap, video) => {
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    const p = video.play();
    if (p && p.then) p.then(() => wrap.classList.add('is-video-playing')).catch(() => {});
  };

  const load = (wrap, video) => {
    const sources = Array.from(video.querySelectorAll('source[data-src]'));
    if (!sources.length) return;
    const max = isPhone() ? 480 : 720;
    sources.sort((a, b) => height(a.dataset.src) - height(b.dataset.src));
    const chosen = sources.filter((s) => height(s.dataset.src) <= max).pop() || sources[0];
    const raw = chosen.dataset.src;
    const light = isPhone() ? lighter(raw) : raw;
    chosen.src = light;
    video.preload = 'auto';
    let retried = false;
    chosen.addEventListener('error', () => {
      if (retried || light === raw) return;
      retried = true;
      chosen.src = raw;
      video.load();
      play(wrap, video);
    });
    video.addEventListener('playing', () => wrap.classList.add('is-video-playing'), { once: true });
    video.addEventListener('canplay', () => play(wrap, video), { once: true });
    video.load();
    play(wrap, video);
    document.addEventListener('visibilitychange', () => { if (!document.hidden && video.paused) play(wrap, video); });
    window.addEventListener('pageshow', () => { if (video.paused) play(wrap, video); });
    document.addEventListener('touchstart', () => { if (video.paused) play(wrap, video); }, { once: true, passive: true });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-nt-hero-video]').forEach((video) => {
      if (video.dataset.ntInit) return;
      video.dataset.ntInit = '1';
      const wrap = video.closest('[data-nt-hero-video-wrap]') || video.parentElement;
      if (!allowed()) { video.remove(); return; }
      const run = () => load(wrap, video);
      const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 600));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
